import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { RefreshCw, ShieldX } from 'lucide-react';
import type { ReactNode } from 'react';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { useHub } from '@/contexts/HubContext';
import { SignOutOverlay, useSignOut } from '@/hooks/useSignOut';
import { platformErrorStatus } from '@/services/context';

/**
 * Full-screen "you have no operator scope" screen shown by the bootstrap gate.
 *
 * Sign-out goes through `useSignOut`, which lands the user on `/login`. A bare
 * `signOut()` leaves the post-logout redirect to AuthKit, which sends a user with
 * no organization to the WorkOS "Couldn't sign in" error page instead.
 */
export function HubAccessDenied() {
  const { isSigningOut, handleSignOut } = useSignOut();

  return (
    <div className="flex h-screen items-center justify-center p-4">
      <SignOutOverlay visible={isSigningOut} />
      <div className="w-full max-w-md">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="error">
              <ShieldX />
            </EmptyMedia>
            <EmptyTitle>Access denied</EmptyTitle>
            <EmptyDescription>
              Your account is not assigned to an operator, so there is nothing here for you to manage. Ask your administrator to grant
              access, then sign in again.
            </EmptyDescription>
            <Button variant="default" className="mt-4" onClick={handleSignOut}>
              Sign out
            </Button>
          </EmptyHeader>
        </Empty>
      </div>
    </div>
  );
}

/** Full-screen bootstrap failure that a retry could clear (5xx, network, timeout). */
export function HubBootstrapFailed({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex h-screen items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="error">
              <RefreshCw />
            </EmptyMedia>
            <EmptyTitle>Couldn't load your workspace</EmptyTitle>
            <EmptyDescription>{message}</EmptyDescription>
            <Button variant="default" className="mt-4" onClick={onRetry}>
              Try again
            </Button>
          </EmptyHeader>
        </Empty>
      </div>
    </div>
  );
}

interface HubBootstrapGateProps {
  children: ReactNode;
}

/**
 * Blocks the Hub shell until session bootstrap resolves an operator context.
 *
 * `HubAuthorizedRoute` only sees JWT roles; a user can carry a Hub role and still be
 * rejected by the Platform API (no `operator_id`, or no FGA grant on any operator).
 * Without this gate the shell renders an empty, broken Apps page instead of saying why.
 *
 * A 401/403 is permanent, so it denies. Anything else that leaves the session without
 * an operator is transient, so it offers a retry: HubContext's bootstrap effect has no
 * other state change that would retrigger it, so rendering the shell here would strand
 * the user in an operator-less Hub with no way back.
 */
export function HubBootstrapGate({ children }: HubBootstrapGateProps) {
  const { loadingState, errorState, operators, retryBootstrap } = useHub();
  const { organizationId } = useAuth();

  // No organization means HubContext never starts the operator fetch, so its loading
  // flag stays true forever — deny rather than hang on a loader that cannot resolve.
  if (organizationId && loadingState.operators) {
    return <Loader text="Loading your workspace..." fullScreen />;
  }

  const status = platformErrorStatus(errorState.operators);
  const forbidden = status === 401 || status === 403;
  const noOperators = !errorState.operators && operators.length === 0;

  if (forbidden || noOperators) {
    return <HubAccessDenied />;
  }

  // A later WorkOS blip must not replace a workspace that already loaded.
  if (errorState.operators && operators.length === 0) {
    return <HubBootstrapFailed message={errorState.operators.message} onRetry={retryBootstrap} />;
  }

  return <>{children}</>;
}
